function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
  for (const v of values) {
    const t = typeof v === 'string' ? v.trim() : ''
    if (t) return t
  }
  return undefined
}

/** When unset, ads stay on (backward compatible). Set to false, 0, no, or off to disable. */
export function parseAdsEnabled(raw: string | undefined): boolean {
  if (raw === undefined || raw === '') return true
  const s = raw.trim().toLowerCase()
  if (['false', '0', 'no', 'off'].includes(s)) return false
  return true
}

export const ADS_ENABLED = parseAdsEnabled(import.meta.env.VITE_ADS_ENABLED)
export const GOOGLE_ADS_ENABLED = ADS_ENABLED && parseAdsEnabled(import.meta.env.VITE_GOOGLE_ADS_ENABLED)

function adsSlot(...values: Array<string | undefined>): string | undefined {
  if (!GOOGLE_ADS_ENABLED) return undefined
  return firstNonEmpty(...values)
}

const defaultSlot = adsSlot(
  import.meta.env.VITE_GOOGLE_ADS_SLOT_DEFAULT,
  import.meta.env.VITE_GOOGLE_ADS_SLOT,
)

/** Pub ID from env, or matches the default site publisher when unset */
export const GOOGLE_ADS_CLIENT =
  firstNonEmpty(import.meta.env.VITE_GOOGLE_ADS_CLIENT) ?? 'ca-pub-6097896022149146'

export const GOOGLE_ADS = {
  client: GOOGLE_ADS_CLIENT,
  sidebar: adsSlot(import.meta.env.VITE_GOOGLE_ADS_SLOT_SIDEBAR, defaultSlot),
  workflowInFeed: adsSlot(import.meta.env.VITE_GOOGLE_ADS_SLOT_WORKFLOW_FEED, defaultSlot),
  workflowDetail: adsSlot(import.meta.env.VITE_GOOGLE_ADS_SLOT_WORKFLOW_DETAIL, defaultSlot),
  guideList: adsSlot(import.meta.env.VITE_GOOGLE_ADS_SLOT_GUIDE_LIST, defaultSlot),
  guideArticle: adsSlot(import.meta.env.VITE_GOOGLE_ADS_SLOT_GUIDE_ARTICLE, defaultSlot),
} as const

const ADSENSE_SCRIPT_ID = 'google-adsense-script'

let adsenseScriptPromise: Promise<void> | null = null

/**
 * Injects the official `adsbygoogle.js` loader once (deduped by id + in-flight promise).
 * Call from ad slots only — avoids loading the script on routes with no ads.
 * Note: Google's loader may still request additional URLs (e.g. `adsbygoogle.js?fcd=true` for consent); that is not duplicate injection from our app.
 */
export function ensureAdsenseLoaderScript(): Promise<void> {
  if (!GOOGLE_ADS_ENABLED || typeof document === 'undefined') return Promise.resolve()
  if (document.getElementById(ADSENSE_SCRIPT_ID)) return Promise.resolve()
  if (adsenseScriptPromise) return adsenseScriptPromise

  adsenseScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = ADSENSE_SCRIPT_ID
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(GOOGLE_ADS_CLIENT)}`
    script.onload = () => resolve()
    script.onerror = () => {
      adsenseScriptPromise = null
      reject(new Error('Failed to load AdSense script'))
    }
    document.head.appendChild(script)
  })

  return adsenseScriptPromise
}

